import React, { Fragment, useContext } from "react";
import {useAuth0} from "@auth0/auth0-react";
import {useQueryClient} from "react-query";
import { Icon } from "@material-ui/core";
import { ActionButton } from "./controls";
import { RoutePathIcon, WorldCheckIcon } from "../../../icons";
import { useTripLegs } from "../../../services/trips";
import { useRequiredPermits } from "../../../services/permits";
import { useFlightJetPlans } from "../../../services/jeppesen";
import AirplaneLandingIcon from "../../../icons/AirplaneLandingIcon";
import AirplaneTakeoffIcon from "../../../icons/AirplaneTakeoffIcon";
import { AirplanemodeActive, SystemUpdateAlt } from "@material-ui/icons";
import TripToolTipStyles from "../TripToolTipStyles";
import { makeStyles } from "@material-ui/core";
import useColour from "../../../services/settings/useColour";
import { ComboSelect } from "../..";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import axios from "axios";
import SystemContext from "../../../SystemContext";
import { viewJetplansPopup } from "../../../modules/Jeppesen/ViewJetplans";

const AccessControl = require ("../../../AccessControl");

const PermitsScreen = (props) => {
  const {tripData, eventBus, colours} = props; 

  const classes = makeStyles(TripToolTipStyles)();
  const apiSitePrefix = getApiSitePrefix();
  const { user } = useAuth0();
  const canUpdatePlans = AccessControl.userHasAccess(user, ["Super Admin", "Admin", "Manage PERMITS"]);
  const SystemData = useContext(SystemContext);

  const queryClient = useQueryClient();
  const extraKeys = [tripData.tripUuid, tripData.uuid];
  const tripLeg = useTripLegs({queryClient, extraKeys});
  const requiredPermitKeys = [tripData.uuid];
  const requiredPermits = useRequiredPermits({queryClient, extraKeys: requiredPermitKeys});
  const jetPlanKeys = [tripData.uuid, 'FORMATTED'];
  const formattedFlightPlan = useFlightJetPlans({queryClient, extraKeys: jetPlanKeys});

  const landingRequiredPermits = [];
  const departureRequiredPermits = [];
  const overflightRequiredPermits = [];

  if (requiredPermits && requiredPermits.data) {
    requiredPermits.data.forEach(requiredPermit => {
      const record = {
        permitType: requiredPermit.permitType,
        countryCode: requiredPermit.countryCode,
        countryName: requiredPermit.country.countrySearchName,
        airportCode: requiredPermit.airportCode,
        permitNo: requiredPermit.permit?.permitNo,
        permitSupplier: requiredPermit.permit?.company?.companyName,
        status: requiredPermit.permit?.status||'REQUIRED',
      }
      switch (requiredPermit.permitType) {
        case 'LANDING': 
          landingRequiredPermits.push(record);
          break;
        case 'DEPARTURE': 
          departureRequiredPermits.push(record);
          break;
        default:
          overflightRequiredPermits.push(record);
          break;
      }
    });
  }

  const showJetPlan = (tripData, planData) => {
    const {airlineCode, flightNo} = tripData;
    const plan = Array.isArray(planData.planData) ? {planText: planData.planData} : planData.planData || {planText: null};

    viewJetplansPopup(plan, `JetPlan for ${airlineCode}${flightNo}`, SystemData);
  }

  const confirmRequestPlan = async (tripLegUuid) => {
    SystemData.confirm(
      `Re-Generate Jet Plan`,
      `Are you sure you wish to re-generate the Flights Jetplan?'`,
      [
        {icon: "check_circle", value: true, label: "Yes", options: {color:"primary"}},
        {icon: "cancel", value: false, label: "No", options: {color:"secondary"}}
      ],
      (response) => {
        if (response) {
          requestPlan(tripLegUuid);
        }
      }
    );
  }

  const requestPlan = async (tripLegUuid) => {
    await axios.put(`${apiSitePrefix}/flightjetplans/request/${tripLegUuid}`);
    queryClient.invalidateQueries(["FlightJetPlans"]);
  }

  const retryPlan = async (flightJetPlanUuid) => {
    await axios.put(`${apiSitePrefix}/flightjetplans/retry/${flightJetPlanUuid}/PLAN`);
    queryClient.invalidateQueries(["FlightJetPlans"]);
  }

  const JetPlan = (props) => {
    const {tripData, planData} = props;
    const {exists} = planData;
    
    return (
      <div>
        <div style={{width: "100%", marginTop: 10}}>
          <div style={{display: "inline-block"}}><RoutePathIcon style={{position: "relative", top: 2}}/></div>
          <div style={{display: "inline-block", textAlign: "left", fontSize: 20, fontWeight: "bold", marginLeft: 3}}>Route Details</div>
          <div style={{display: "inline-block", float:"right", textAlign: "right"}}>
            {(!exists || (exists && planData?.status===null)) && canUpdatePlans?
              <ActionButton 
                variant="outlined" 
                startIcon={<Icon>download_for_offline</Icon>}
                onClick={()=>{
                  requestPlan(tripData.uuid);
                }}
              >
                Request
              </ActionButton>
            : planData?.status === "FAILED" && canUpdatePlans? 
              <ActionButton 
                variant="outlined" 
                startIcon={<Icon>cached</Icon>}
                onClick={()=>{
                  retryPlan(planData.flightJetPlanUuid);
                }}
              >
                Retry
              </ActionButton>
            : planData?.status === "COMPLETED" && canUpdatePlans?
              <ActionButton 
                variant="outlined" 
                startIcon={<Icon>refresh</Icon>}
                onClick={()=>{
                  confirmRequestPlan(tripData.uuid);
                }}
              >
                Re-Generate
              </ActionButton>
            : null} 
          </div>
        </div>
        {!exists ?
          <div style={{fontSize: 16, fontWeight: "bold", padding: 10, textAlign: "center", border: "1px solid #ffffff", color: "#ffffff", backgroundColor: "#ff0000"}}>
            A plan does not exist for this flight
          </div>
        :null}
        {exists && planData?.status === "FAILED"?
          <div style={{fontSize: 16, lineHeight: "normal", fontWeight: "bold", padding: 10, textAlign: "center", border: "1px solid #ffffff", color: "#ffffff", backgroundColor: "#ff0000"}}>
            Failed - {planData.errorText}
          </div>      
        :null}
        {exists && (planData?.status === "REQUIRED" || planData?.status === "CALCULATING" || planData?.status === "STAGE1" || planData?.status === "STAGE2")?
          <div style={{fontSize: 16, fontWeight: "bold", padding: 10, textAlign: "center", border: "1px solid #ffffff", color: "#000000", backgroundColor: "#dddddd"}}>
            Plan has been requested
          </div>      
        :null}
        {exists && planData?.status === "COMPLETED"?
          <div style={{position: "relative", fontSize: 12, whiteSpace: "pre-line", padding: 10, textAlign: "left", border: "1px solid #ffffff", color: "#000000", backgroundColor: "#dddddd"}}>
            <span>{planData.routeText}</span>
            <div onClick={()=> showJetPlan(tripData, planData)} style={{cursor: "pointer", position: "absolute", top: 1, right: 1, fontSize: "12px"}}><Icon fontSize={"inherit"}>aspect_ratio</Icon></div>
          </div>      
        :null}
      </div>
    )
  }

  const PermitRow = (props) => {
    const backgroundColor = useColour({code: 'PERMITS BACKGROUND', status: props.status});
    const color = useColour({code: 'PERMITS TEXT', status: props.status});
    return (
      <div className={classes.permitRow} style={{backgroundColor, color}}>
        <div style={{fontWeight: "700", display: "inline-block", width: "30%"}}>{props.countryName}</div>
        <div style={{display: "inline-block", width: "48%"}}>{props.permitNo}</div>
        <div style={{display: "inline-block", width: "22%"}}>         
          <ComboSelect
            group="PERMIT STATUS"
            subGroup=""
            value={props.status}
            displayOnly={true}
          />
        </div>
      </div>
    );
  }

  return (<Fragment>
    <div style={{width: "100%", paddingBottom: 5}}>
      <div style={{display: "inline-block"}}><WorldCheckIcon style={{position: "relative", top: 2}}/></div>
      <div style={{display: "inline-block", textAlign: "left", fontSize: 20, fontWeight: "bold", marginLeft: 3}}>Permit Details</div>
      {/* <div style={{display: "inline-block", float:"right", textAlign: "right"}}>
        <ActionButton 
          variant="outlined" 
          startIcon={<Icon>edit</Icon>}
          onClick={()=>{
            eventBus.trigger("RouteView.TimeWindow.TripAction.editPermits",{tripData: {...tripLeg.data}, extraKeys});
          }}
        >
          Edit
        </ActionButton>
      </div> */}
    </div>
    <div className={classes.permitTable}>
      {departureRequiredPermits.length > 0?(
      <div className={classes.permitTypeHeader}>
        <div style={{display: "inline-block"}}><AirplaneTakeoffIcon style={{position: "relative", top: 2, width: 16, height: 16}}/></div>
        <div style={{display: "inline-block", textAlign: "left", fontSize: 16, fontWeight: "bold", marginLeft: 3}}>Departure Permit</div>
      </div>
      ): null}
      {(departureRequiredPermits||[]).map(record => (
        <PermitRow countryName={record.countryName} permitNo={record.permitNo} status={record.status}/>
      ))}
      <div className={classes.permitTypeHeader}>
        <div style={{display: "inline-block"}}><AirplanemodeActive style={{position: "relative", top: 2, width: 16, height: 16}}/></div>
        <div style={{display: "inline-block", textAlign: "left", fontSize: 16, fontWeight: "bold", marginLeft: 3}}>Overflight Permits</div>
      </div>
      {(overflightRequiredPermits||[]).map(record => (
        <PermitRow countryName={record.countryName} permitNo={record.permitNo} status={record.status}/>
      ))}
      <div className={classes.permitTypeHeader}>
        <div style={{display: "inline-block"}}><AirplaneLandingIcon style={{position: "relative", top: 2, width: 16, height: 16}}/></div>
        <div style={{display: "inline-block", textAlign: "left", fontSize: 16, fontWeight: "bold", marginLeft: 3}}>Landing Permit</div>
      </div>
      {(landingRequiredPermits||[]).map(record => (
        <PermitRow countryName={record.countryName} permitNo={record.permitNo} status={record.status}/>
      ))}
    </div>
    {formattedFlightPlan && formattedFlightPlan.data?
      <JetPlan planData={formattedFlightPlan.data} tripData={tripData}/>
    : <>No Plan Data</>}

  </Fragment>); 
}

export default PermitsScreen;